import type { VerificationSummary } from '@/types/api';

const STORAGE_KEY = 'irrigo:verification-pending';
export const VERIFICATION_PROGRESS_EVENT = 'irrigo:verification-progress';

/** ~10 min por slot (LoRa Class A: 1 downlink/uplink), mínimo 2 min */
const MS_PER_SLOT = 10 * 60_000;
const MIN_WAIT_MS = 2 * 60_000;
/** Margen extra sobre la estimación */
const WAIT_BUFFER_MS = 15 * 60_000;

export const LORA_VERIFY_POLL_MS = 30_000;

export interface VerificationPendingSession {
  controllerId: string;
  controllerName?: string;
  startedAt: number;
  expiresAt: number;
  total: number;
  /** verified + mismatch + missing + error (slots ya contrastados) */
  lastKnownResolved: number;
  lastKnownVerified: number;
  isLora: boolean;
}

export interface VerificationProgressDetail {
  controllerId: string;
  controllerName?: string;
  total: number;
  resolved: number;
  verified: number;
  pending: number;
  completed: boolean;
  /** true si avanzó respecto a la última marca conocida */
  progressed: boolean;
}

export function estimateLoraVerifyMs(scheduleCount: number): number {
  const n = Math.max(1, scheduleCount);
  return Math.max(MIN_WAIT_MS, n * MS_PER_SLOT) + WAIT_BUFFER_MS;
}

/** Texto humano de ETA (sin buffer) para toasts/banner */
export function formatLoraVerifyEta(scheduleCount: number): string {
  const n = Math.max(1, scheduleCount);
  if (n <= 1) return 'unos segundos a ~1 minuto';
  const minutes = n * 10;
  if (minutes < 60) return `aprox. ${minutes} minutos`;
  const hours = Math.round((minutes / 60) * 10) / 10;
  return `aprox. ${hours} hora${hours >= 1.5 ? 's' : ''}`;
}

export function countResolved(summary: VerificationSummary): number {
  return (
    (summary.verified ?? 0) +
    (summary.mismatch ?? 0) +
    (summary.missing ?? 0) +
    (summary.error ?? 0)
  );
}

export function countInProgress(summary: VerificationSummary): number {
  return (
    (summary.pending ?? 0) + (summary.sent ?? 0) + (summary.confirmed ?? 0)
  );
}

export function isTransitionalSummary(summary?: VerificationSummary | null): boolean {
  if (!summary) return false;
  return countInProgress(summary) > 0;
}

function readAll(): Record<string, VerificationPendingSession> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, VerificationPendingSession>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, VerificationPendingSession>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    window.dispatchEvent(new Event('irrigo:verification-sessions-changed'));
  } catch {
    // ignore quota / private mode
  }
}

export function getVerificationSession(
  controllerId: string
): VerificationPendingSession | null {
  const all = readAll();
  const session = all[controllerId];
  if (!session) return null;
  if (Date.now() > session.expiresAt) {
    delete all[controllerId];
    writeAll(all);
    return null;
  }
  return session;
}

export function listActiveVerificationSessions(): VerificationPendingSession[] {
  const all = readAll();
  const now = Date.now();
  let changed = false;
  const active: VerificationPendingSession[] = [];
  for (const [id, session] of Object.entries(all)) {
    if (now > session.expiresAt) {
      delete all[id];
      changed = true;
      continue;
    }
    active.push(session);
  }
  if (changed) writeAll(all);
  return active;
}

export function startVerificationSession(params: {
  controllerId: string;
  controllerName?: string;
  total: number;
  summary?: VerificationSummary;
  isLora?: boolean;
}): VerificationPendingSession {
  const isLora = params.isLora ?? true;
  const total = Math.max(1, params.total || params.summary?.total || 1);
  const waitMs = isLora ? estimateLoraVerifyMs(total) : MIN_WAIT_MS;
  const summary = params.summary;
  const session: VerificationPendingSession = {
    controllerId: params.controllerId,
    controllerName: params.controllerName,
    startedAt: Date.now(),
    expiresAt: Date.now() + waitMs,
    total,
    lastKnownResolved: summary ? countResolved(summary) : 0,
    lastKnownVerified: summary?.verified ?? 0,
    isLora,
  };
  const all = readAll();
  all[params.controllerId] = session;
  writeAll(all);
  return session;
}

export function updateVerificationSessionProgress(
  controllerId: string,
  summary: VerificationSummary
): VerificationProgressDetail | null {
  const all = readAll();
  const session = all[controllerId];
  if (!session) return null;

  const resolved = countResolved(summary);
  const verified = summary.verified ?? 0;
  const pending = countInProgress(summary);
  const progressed =
    resolved > session.lastKnownResolved || verified > session.lastKnownVerified;
  const completed = pending === 0;

  session.lastKnownResolved = resolved;
  session.lastKnownVerified = verified;
  session.total = Math.max(session.total, summary.total ?? session.total);

  if (completed) {
    delete all[controllerId];
  } else {
    all[controllerId] = session;
  }
  writeAll(all);

  const detail: VerificationProgressDetail = {
    controllerId,
    controllerName: session.controllerName,
    total: session.total,
    resolved,
    verified,
    pending,
    completed,
    progressed,
  };

  if (progressed || completed) {
    window.dispatchEvent(
      new CustomEvent<VerificationProgressDetail>(VERIFICATION_PROGRESS_EVENT, {
        detail,
      })
    );
  }

  return detail;
}

export function clearVerificationSession(controllerId: string) {
  const all = readAll();
  if (!all[controllerId]) return;
  delete all[controllerId];
  writeAll(all);
}

export function hasActiveVerificationSession(controllerId: string): boolean {
  return getVerificationSession(controllerId) != null;
}
